/**
 * BOMApprovalPage - BOM Tree Approval Step for Assembly Analysis
 *
 * This page displays the reconstructed BOM (Bill of Materials) tree
 * and allows users to review and approve before final classification.
 *
 * 12-25 Requirement 4.4.2 - Phase 2: Assembly Review
 */

import { useState, useEffect, useRef } from 'react';
import ThumbnailImage from './ThumbnailImage';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaExclamationTriangle, FaChevronRight, FaChevronDown, FaChevronUp, FaRobot } from 'react-icons/fa';
import { FiPackage, FiFile, FiAlertCircle, FiCheckCircle, FiLoader, FiDownload, FiExternalLink, FiList, FiLayers, FiFileText, FiInfo } from 'react-icons/fi';
import { IoCubeOutline } from 'react-icons/io5';
import { HiArrowDownTray } from 'react-icons/hi2';
import axios from 'axios';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import MaterialWeightSummary from './MaterialWeightSummary';

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

/**
 * Normalize raw material names to parent categories for grouping.
 * Maps specific material names (e.g., "Ultraform H4320") to parent categories (e.g., "Plastics").
 */
const normalizeMaterialToParent = (material) => {
    if (!material) return 'Other/Unknown';

    const m = material.toLowerCase().trim();

    // Steel variants
    if (m.includes('steel') || m.includes('stainless') || m.includes('iron') ||
        m.includes('ferrous') || m.includes('s355') || m.includes('s235') ||
        m.includes('dc01') || m.includes('dd11') || m.includes('dx51') ||
        m.includes('galvanized') || m.includes('hot rolled') || m.includes('cold rolled')) {
        return 'Steel';
    }

    // Aluminum variants
    if (m.includes('aluminum') || m.includes('aluminium') || m.includes('al ') ||
        m.includes('al-') || m.match(/^al\d/) || m.includes('almg') ||
        m.includes('6061') || m.includes('6063') || m.includes('5052') ||
        m.includes('5083') || m.includes('7075') || m.includes('2024')) {
        return 'Aluminum';
    }

    // Copper/Brass variants
    if (m.includes('copper') || m.includes('brass') || m.includes('bronze') ||
        m.includes('cuzn') || m.includes('cu-') || m.includes('beryllium')) {
        return 'Copper/Brass';
    }

    // Plastics/Polymers - comprehensive list
    if (m.includes('plastic') || m.includes('polymer') || m.includes('nylon') ||
        m.includes('polyamide') || m.includes('pa6') || m.includes('pa66') ||
        m.includes('polypropylene') || m.includes('polyethylene') || m.includes('pvc') ||
        m.includes('polycarbonate') || m.includes('abs') || m.includes('acetal') ||
        m.includes('pom') || m.includes('ultraform') || m.includes('delrin') ||
        m.includes('teflon') || m.includes('ptfe') || m.includes('peek') ||
        m.includes('pps') || m.includes('pet') || m.includes('hdpe') || m.includes('ldpe') ||
        m.includes('zytel') || m.includes('akulon') || m.includes('foam') ||
        m.includes('urethane') || m.includes('poron') || m.includes('plastazote') ||
        m.includes('armaflex') || m.includes('tpe') || m.includes('silicone') ||
        m.includes('rubber') || m.includes('epdm') || m.includes('nbr') ||
        m.includes('elastomer') || m.includes('exolon') || m.includes('polyvantis') ||
        m.includes('lastrocel') || m.includes('cellular') || m.includes('expanded')) {
        return 'Plastics/Polymers';
    }

    // Paper/Cardboard
    if (m.includes('paper') || m.includes('cardboard') || m.includes('carton') ||
        m.includes('corrugated') || m.includes('fasson') || m.includes('label') ||
        m.includes('viledon') || m.includes('filter')) {
        return 'Paper/Fiber';
    }

    // Glass/Ceramics
    if (m.includes('glass') || m.includes('ceramic') || m.includes('quartz')) {
        return 'Glass/Ceramics';
    }

    // Other metals
    if (m.includes('titanium') || m.includes('nickel') || m.includes('zinc') ||
        m.includes('tin') || m.includes('magnesium') || m.includes('lead')) {
        return 'Other Metals';
    }

    // Multi-material / Assembly
    if (m.includes('assembly') || m.includes('multiple material') || m.includes('see bom') ||
        m.includes('various')) {
        return 'Multi-Material';
    }

    // Not specified / Unknown
    if (m.includes('not specified') || m.includes('unspecified') || m.includes('unknown') ||
        m.includes('n/a') || m === 'other' || m === 'na' || m === 'none') {
        return 'Other/Unknown';
    }

    // Default fallback - group unrecognized materials as Other/Unknown
    return 'Other/Unknown';
};

// Tooltip messages
const MATCHED_TOOLTIP = "This BOM item was matched to an uploaded drawing file. Click to view the file card with detailed analysis including HTS classification, material specifications, and tariff information.";
const MISSING_TOOLTIP = "No matching drawing file was found for this BOM item. The part number from the parent assembly's BOM could not be linked to any uploaded document. Consider uploading the missing drawing or verify the part number is correct.";

// File Card component for displaying matched document details
function FileCard({ doc, highlighted }) {
    const cardRef = useRef(null);
    const token = sessionStorage.getItem("userToken");

    useEffect(() => {
        if (highlighted && cardRef.current) {
            cardRef.current.classList.add('ring-2', 'ring-[#1e5045]', 'ring-offset-2');
            const timer = setTimeout(() => {
                cardRef.current?.classList.remove('ring-2', 'ring-[#1e5045]', 'ring-offset-2');
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [highlighted]);

    if (!doc) return null;

    return (
        <div
            ref={cardRef}
            id={`file-card-${doc.file_id}`}
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300"
        >
            <div className="flex">
                {/* Thumbnail - clickable to open PDF */}
                <div
                    className={`w-32 h-40 flex-shrink-0 bg-gray-100 flex items-center justify-center overflow-hidden ${doc.s3_url ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                    onClick={() => doc.s3_url && window.open(doc.s3_url, '_blank', 'noopener,noreferrer')}
                    title={doc.s3_url ? "Click to open PDF" : ""}
                >
                    {doc.s3_url ? (
                        <ThumbnailImage
                            fileId={doc.file_id}
                            filename={doc.filename}
                            s3Url={doc.s3_url}
                        />
                    ) : (
                        <FiFile className="w-12 h-12 text-gray-300" />
                    )}
                </div>

                {/* Details */}
                <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                        <div>
                            <h4 className="font-semibold text-gray-900 truncate max-w-xs">
                                {doc.part_number || doc.filename}
                            </h4>
                            <p className="text-sm text-gray-500 truncate max-w-xs">
                                {doc.description || doc.filename}
                            </p>
                        </div>
                        {doc.s3_url && (
                            <a
                                href={doc.s3_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-gray-400 hover:text-[#1e5045] transition-colors"
                                title="Open PDF"
                            >
                                <FiExternalLink className="w-4 h-4" />
                            </a>
                        )}
                    </div>

                    {/* Analysis Summary */}
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        {doc.hts_code && (
                            <div>
                                <span className="text-gray-500">HTS:</span>{' '}
                                <span className="font-mono font-medium text-gray-900">{doc.hts_code}</span>
                            </div>
                        )}
                        {doc.duty_rate !== undefined && doc.duty_rate > 0 && (
                            <div>
                                <span className="text-gray-500">Duty:</span>{' '}
                                <span className="font-medium text-gray-900">{doc.duty_rate}%</span>
                            </div>
                        )}
                        {doc.material && (
                            <div>
                                <span className="text-gray-500">Material:</span>{' '}
                                <span className="text-gray-900">{doc.material}</span>
                            </div>
                        )}
                        {doc.weight_kg && (
                            <div>
                                <span className="text-gray-500">Weight:</span>{' '}
                                <span className="text-gray-900">{doc.weight_kg} kg</span>
                            </div>
                        )}
                    </div>

                    {/* Tariff Flags */}
                    <div className="mt-2 flex items-center gap-2">
                        {doc.sec232_applies && (
                            <span className="text-xs bg-[#f26522]/20 text-[#f26522] px-2 py-0.5 rounded-full">
                                Sec 232
                            </span>
                        )}
                        {doc.sec301_applies && (
                            <span className="text-xs bg-[#f26522]/15 text-[#f26522] px-2 py-0.5 rounded-full">
                                Sec 301
                            </span>
                        )}
                        {doc.confidence && (() => {
                            const conf = Number(doc.confidence);
                            const normalized = conf > 1 ? conf / 100 : conf;
                            return (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    normalized >= 0.8 ? 'bg-[#94c43d]/20 text-[#1e5045]' :
                                    normalized >= 0.5 ? 'bg-[#f26522]/20 text-[#f26522]' :
                                    'bg-gray-100 text-gray-700'
                                }`}>
                                    {Math.round(normalized * 100)}% confidence
                                </span>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Recursive component to render BOM tree nodes
function BOMTreeNode({ node, level = 0, onNodeClick, qtyEdits, onQtyChange, isRoot = false, userSpecifiedRoot = false }) {
    const [expanded, setExpanded] = useState(true);
    const hasChildren = node.children && node.children.length > 0;
    const isMatched = node.status === 'matched';

    // Get effective qty (edited value or original, default to 1)
    const nodeKey = node.part_number || node.file_id || `node-${level}`;
    const effectiveQty = qtyEdits?.[nodeKey] ?? node.qty ?? 1;

    const handleClick = () => {
        if (isMatched && node.matched_doc?.file_id) {
            onNodeClick(node.matched_doc.file_id);
        }
    };

    const handleQtyChange = (e) => {
        e.stopPropagation();
        const newQty = parseInt(e.target.value, 10);
        if (!isNaN(newQty) && newQty >= 1 && onQtyChange) {
            onQtyChange(nodeKey, newQty);
        }
    };

    return (
        <div className="select-none">
            <div
                className={`flex items-center gap-2 py-2 px-3 rounded-lg transition-colors ${
                    isMatched
                        ? 'hover:bg-[#94c43d]/10 cursor-pointer'
                        : 'hover:bg-[#f26522]/10'
                }`}
                style={{ marginLeft: `${level * 24}px` }}
                onClick={isMatched ? handleClick : undefined}
            >
                {/* Expand/Collapse Toggle */}
                {hasChildren ? (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(!expanded);
                        }}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                        {expanded ? (
                            <FaChevronDown className="w-3 h-3 text-gray-500" />
                        ) : (
                            <FaChevronRight className="w-3 h-3 text-gray-500" />
                        )}
                    </button>
                ) : (
                    <span className="w-5" />
                )}

                {/* Status Icon */}
                {isMatched ? (
                    <FiCheckCircle className="w-5 h-5 text-[#94c43d] flex-shrink-0" />
                ) : (
                    <FiAlertCircle className="w-5 h-5 text-[#f26522] flex-shrink-0" />
                )}

                {/* Part Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        {/* Top Level Assembly Badge */}
                        {isRoot && (
                            <span className={`px-2 py-0.5 text-xs font-bold rounded ${userSpecifiedRoot ? 'bg-yellow-400 text-yellow-900' : 'bg-[#1e5045] text-white'}`}>
                                {userSpecifiedRoot ? 'TOP LEVEL' : 'ROOT'}
                            </span>
                        )}
                        <span className="font-mono font-medium text-gray-900 truncate">
                            {node.part_number || 'Unknown Part'}
                        </span>
                        {/* Editable Qty Field */}
                        <div className="flex items-center gap-1 text-xs">
                            <span className="text-gray-500">×</span>
                            <input
                                type="number"
                                min="1"
                                value={effectiveQty}
                                onChange={handleQtyChange}
                                onClick={(e) => e.stopPropagation()}
                                className="w-12 px-1.5 py-0.5 text-center border border-gray-300 rounded bg-white text-gray-700 focus:border-[#1e5045] focus:ring-1 focus:ring-[#1e5045] focus:outline-none"
                                title="Quantity - click to edit"
                            />
                        </div>
                    </div>
                    {node.description && (
                        <p className="text-sm text-gray-500 truncate">{node.description}</p>
                    )}
                </div>

                {/* Status Badge with Tooltip */}
                <span
                    className={`text-xs font-medium px-2 py-1 rounded-full cursor-help ${
                        isMatched
                            ? 'bg-[#94c43d]/20 text-[#1e5045]'
                            : 'bg-[#f26522]/20 text-[#f26522]'
                    }`}
                    title={isMatched ? MATCHED_TOOLTIP : MISSING_TOOLTIP}
                >
                    {isMatched ? 'Matched' : 'Missing'}
                </span>

                {/* Link indicator for matched items */}
                {isMatched && node.matched_doc?.file_id && (
                    <FiExternalLink className="w-4 h-4 text-gray-400" title="Click to view file card" />
                )}
            </div>

            {/* Children */}
            {expanded && hasChildren && (
                <div className="border-l-2 border-gray-200 ml-5">
                    {node.children.map((child, idx) => (
                        <BOMTreeNode
                            key={`${child.part_number}-${idx}`}
                            node={child}
                            level={level + 1}
                            onNodeClick={onNodeClick}
                            qtyEdits={qtyEdits}
                            onQtyChange={onQtyChange}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * ConstructionReportBanner - Displays how the BOM was constructed
 * Shows script summary, LLM narrative, corrections applied, and warnings
 */
function ConstructionReportBanner({ report, isExpanded, onToggle }) {
    const [correctionsExpanded, setCorrectionsExpanded] = useState(false);

    if (!report) return null;

    const { script_summary, llm_narrative, corrections_applied, excluded_documents, excluded_count, invalid_bom_items, invalid_bom_items_removed, invalid_bom_items_count, hierarchy_corrections, hierarchy_corrections_count, warnings, quality_assessment, llm_review_available } = report;

    // Determine quality badge color
    const getQualityColor = (quality) => {
        switch (quality) {
            case 'good': return 'bg-[#94c43d]/20 text-[#1e5045]';
            case 'partial': return 'bg-[#f26522]/20 text-[#f26522]';
            case 'poor': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const successfulCorrections = corrections_applied?.filter(c => c.result === 'success') || [];

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
            {/* Header - Always visible */}
            <div
                className="p-4 border-b border-gray-100 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={onToggle}
            >
                <div className="flex items-center gap-3">
                    <FiFileText className="w-5 h-5 text-[#1e5045]" />
                    <h3 className="text-lg font-semibold text-gray-900">
                        BOM Construction Report
                    </h3>
                    {quality_assessment?.overall && (
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getQualityColor(quality_assessment.overall)}`}>
                            {quality_assessment.overall} quality
                        </span>
                    )}
                    {llm_review_available && (
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 font-medium flex items-center gap-1">
                            <FaRobot className="w-3 h-3" />
                            AI Reviewed
                        </span>
                    )}
                </div>
                <FaChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </div>

            {/* Collapsible content */}
            {isExpanded && (
                <div className="p-4 space-y-4">
                    {/* Script Summary Section */}
                    <div className="bg-[#f6f4ed] rounded-lg p-4">
                        <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                            <FiInfo className="w-4 h-4" />
                            Reconstruction Summary
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div>
                                <span className="text-gray-500">Matched:</span>
                                <span className="ml-2 font-semibold text-[#94c43d]">
                                    {script_summary?.matched_count || 0}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-500">Missing:</span>
                                <span className="ml-2 font-semibold text-[#f26522]">
                                    {script_summary?.missing_count || 0}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-500">Orphans:</span>
                                <span className="ml-2 font-semibold text-gray-700">
                                    {script_summary?.orphan_count || 0}
                                </span>
                            </div>
                            {(script_summary?.excluded_count > 0 || excluded_count > 0) && (
                                <div>
                                    <span className="text-gray-500">Excluded:</span>
                                    <span className="ml-2 font-semibold text-purple-600">
                                        {script_summary?.excluded_count || excluded_count || 0}
                                    </span>
                                </div>
                            )}
                            {(script_summary?.hierarchy_corrections > 0 || hierarchy_corrections_count > 0) && (
                                <div>
                                    <span className="text-gray-500">Hierarchy Fixes:</span>
                                    <span className="ml-2 font-semibold text-blue-600">
                                        {script_summary?.hierarchy_corrections || hierarchy_corrections_count || 0}
                                    </span>
                                </div>
                            )}
                            {(script_summary?.invalid_bom_items_count > 0 || invalid_bom_items_count > 0) && (
                                <div>
                                    <span className="text-gray-500">Invalid Items:</span>
                                    <span className="ml-2 font-semibold text-red-600">
                                        {script_summary?.invalid_bom_items_count || invalid_bom_items_count || 0}
                                    </span>
                                </div>
                            )}
                            <div>
                                <span className="text-gray-500">Source:</span>
                                <span className="ml-2 font-semibold text-gray-700">
                                    {script_summary?.hierarchy_source || 'unknown'}
                                </span>
                            </div>
                        </div>
                        {script_summary?.match_methods && Object.keys(script_summary.match_methods).length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                                <span className="text-xs text-gray-500">Match methods: </span>
                                <span className="text-xs text-gray-700">
                                    {Object.entries(script_summary.match_methods)
                                        .map(([method, count]) => `${count} ${method.replace('_', ' ')}`)
                                        .join(' · ')}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* LLM Narrative Section */}
                    {llm_narrative && (
                        <div>
                            <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <FaRobot className="w-4 h-4 text-purple-600" />
                                AI Analysis
                            </h4>
                            <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                                {llm_narrative}
                            </p>
                        </div>
                    )}

                    {/* Corrections Applied Section - Expandable */}
                    {successfulCorrections.length > 0 && (
                        <div className="bg-green-50 rounded-lg p-4">
                            <button
                                onClick={() => setCorrectionsExpanded(!correctionsExpanded)}
                                className="w-full font-medium text-green-800 mb-2 flex items-center justify-between hover:text-green-900 transition-colors"
                            >
                                <span className="flex items-center gap-2">
                                    <FiCheckCircle className="w-4 h-4" />
                                    AI Corrections Applied ({successfulCorrections.length})
                                </span>
                                {correctionsExpanded ? (
                                    <FaChevronUp className="w-3 h-3 text-green-600" />
                                ) : (
                                    <FaChevronDown className="w-3 h-3 text-green-600" />
                                )}
                            </button>
                            <ul className="text-sm text-green-700 space-y-1">
                                {(correctionsExpanded ? successfulCorrections : successfulCorrections.slice(0, 5)).map((correction, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                        <span className="text-green-500 mt-0.5">•</span>
                                        <span>
                                            <strong>{correction.action}</strong> on {correction.target}
                                            {correction.reasoning && (
                                                <span className="text-green-600"> — {correction.reasoning}</span>
                                            )}
                                        </span>
                                    </li>
                                ))}
                                {!correctionsExpanded && successfulCorrections.length > 5 && (
                                    <li className="text-green-600 italic pt-1">
                                        <button
                                            onClick={() => setCorrectionsExpanded(true)}
                                            className="hover:text-green-800 hover:underline transition-colors"
                                        >
                                            Show all {successfulCorrections.length} corrections...
                                        </button>
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}

                    {/* Excluded Documents Section - Non-part documents weeded out by AI */}
                    {excluded_documents && excluded_documents.length > 0 && (
                        <div className="bg-purple-50 rounded-lg p-4">
                            <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                                <FiFileText className="w-4 h-4" />
                                Non-Part Documents Excluded ({excluded_documents.length})
                            </h4>
                            <p className="text-xs text-purple-600 mb-2">
                                The AI identified these documents as supporting materials, not actual parts/components.
                            </p>
                            <ul className="text-sm text-purple-700 space-y-2">
                                {excluded_documents.map((doc, idx) => (
                                    <li key={idx} className="flex items-start gap-2 bg-white/50 rounded p-2">
                                        <span className="text-purple-400 mt-0.5">✕</span>
                                        <div>
                                            <span className="font-medium">{doc.filename}</span>
                                            <span className="mx-2 text-purple-400">·</span>
                                            <span className="text-purple-600 text-xs px-2 py-0.5 bg-purple-100 rounded">
                                                {doc.document_type}
                                            </span>
                                            {doc.reason && (
                                                <p className="text-xs text-purple-500 mt-1">{doc.reason}</p>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Hierarchy Corrections Section - Illogical parent-child relationships fixed by AI */}
                    {hierarchy_corrections && hierarchy_corrections.length > 0 && (
                        <div className="bg-blue-50 rounded-lg p-4">
                            <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                                <FiInfo className="w-4 h-4" />
                                Hierarchy Corrections ({hierarchy_corrections.length})
                            </h4>
                            <p className="text-xs text-blue-600 mb-2">
                                The AI identified and fixed illogical parent-child relationships (e.g., a grommet appearing as parent to a subassembly).
                            </p>
                            <ul className="text-sm text-blue-700 space-y-2">
                                {hierarchy_corrections.map((correction, idx) => (
                                    <li key={idx} className="flex items-start gap-2 bg-white/50 rounded p-2">
                                        <span className="text-blue-400 mt-0.5">↳</span>
                                        <div>
                                            <span className="font-medium">{correction.target}</span>
                                            <span className="mx-2 text-blue-400">→</span>
                                            <span className="text-blue-600 text-xs">
                                                {correction.details?.children_moved || 0} children moved
                                            </span>
                                            {correction.reasoning && (
                                                <p className="text-xs text-blue-500 mt-1">{correction.reasoning}</p>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Invalid BOM Items Section - Incorrectly extracted items removed by AI */}
                    {((invalid_bom_items && invalid_bom_items.length > 0) || (invalid_bom_items_removed && invalid_bom_items_removed.length > 0)) && (
                        <div className="bg-red-50 rounded-lg p-4">
                            <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                                <FiInfo className="w-4 h-4" />
                                Invalid BOM Items Removed ({invalid_bom_items?.length || invalid_bom_items_removed?.length || 0})
                            </h4>
                            <p className="text-xs text-red-600 mb-2">
                                The AI identified these as incorrectly extracted items (OCR errors, drawing notes, table headers, etc.) that are not real part numbers.
                            </p>
                            <ul className="text-sm text-red-700 space-y-2">
                                {(invalid_bom_items || []).map((item, idx) => (
                                    <li key={`invalid-${idx}`} className="flex items-start gap-2 bg-white/50 rounded p-2">
                                        <span className="text-red-400 mt-0.5">✕</span>
                                        <div>
                                            <span className="font-medium font-mono text-xs bg-red-100 px-1 rounded">{item.extracted_text}</span>
                                            <span className="mx-2 text-red-400">·</span>
                                            <span className="text-red-600 text-xs">{item.likely_source}</span>
                                            {item.reason && (
                                                <p className="text-xs text-red-500 mt-1">{item.reason}</p>
                                            )}
                                        </div>
                                    </li>
                                ))}
                                {(invalid_bom_items_removed || []).map((correction, idx) => (
                                    <li key={`removed-${idx}`} className="flex items-start gap-2 bg-white/50 rounded p-2">
                                        <span className="text-red-400 mt-0.5">✕</span>
                                        <div>
                                            <span className="font-medium font-mono text-xs bg-red-100 px-1 rounded">{correction.target}</span>
                                            <span className="mx-2 text-red-400">·</span>
                                            <span className="text-red-600 text-xs">{correction.details?.likely_source || 'Unknown'}</span>
                                            {correction.reasoning && (
                                                <p className="text-xs text-red-500 mt-1">{correction.reasoning}</p>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Warnings Section */}
                    {warnings && warnings.length > 0 && (
                        <div className="bg-[#f26522]/10 rounded-lg p-4">
                            <h4 className="font-medium text-[#f26522] mb-2 flex items-center gap-2">
                                <FaExclamationTriangle className="w-4 h-4" />
                                Warnings
                            </h4>
                            <ul className="text-sm text-gray-700 space-y-1">
                                {warnings.map((warning, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                        <span className="text-[#f26522] mt-0.5">•</span>
                                        {warning}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Recommendations */}
                    {quality_assessment?.recommendations?.length > 0 && (
                        <div className="border-t border-gray-100 pt-4">
                            <h4 className="font-medium text-gray-700 mb-2">
                                Recommendations
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                                {quality_assessment.recommendations.map((rec, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                        <FiCheckCircle className="w-4 h-4 text-[#1e5045] mt-0.5 flex-shrink-0" />
                                        {rec}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* No LLM review available */}
                    {!llm_review_available && (
                        <div className="text-sm text-gray-500 italic">
                            AI review was not available for this analysis. The BOM was constructed using rule-based matching only.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function BOMApprovalPage() {
    const { assemblyId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [approving, setApproving] = useState(false);
    const [error, setError] = useState(null);
    const [highlightedCard, setHighlightedCard] = useState(null);
    const [excludeMissing, setExcludeMissing] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [qtyEdits, setQtyEdits] = useState({}); // Track qty edits by part_number
    const [viewMode, setViewMode] = useState('bom'); // 'bom' or 'material'
    const [expandedMaterials, setExpandedMaterials] = useState({}); // Track which material categories are expanded
    const [reportExpanded, setReportExpanded] = useState(true); // Construction report expansion state
    const token = sessionStorage.getItem("userToken");

    // Handle qty change from tree node
    const handleQtyChange = (partNumber, newQty) => {
        setQtyEdits(prev => ({
            ...prev,
            [partNumber]: newQty
        }));
    };

    // Get passed state from navigation
    const passedState = location.state || {};
    const folderName = passedState.folderName;
    const spacePath = passedState.spacePath;
    const fileIds = passedState.fileIds || [];

    useEffect(() => {
        if (!assemblyId) return;

        const fetchAnalysis = async () => {
            try {
                setLoading(true);
                const res = await axios.get(
                    `${BASE_URL}/ocr/assembly-analysis/${assemblyId}/`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                console.log('[BOM DEBUG] API response:', {
                    hasResult: !!res.data?.result,
                    hasAssemblyTree: !!res.data?.result?.assembly_tree,
                    orphanCount: res.data?.result?.assembly_tree?.orphan_components?.length || 0,
                    matchedCount: res.data?.result?.assembly_tree?.matched_count
                });
                setAnalysis(res.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch assembly analysis:', err);
                setError(err.response?.data?.error || 'Failed to load assembly analysis');
            } finally {
                setLoading(false);
            }
        };

        fetchAnalysis();
    }, [assemblyId, token]);

    // Handle click on BOM node to scroll to file card
    const handleNodeClick = (fileId) => {
        const element = document.getElementById(`file-card-${fileId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedCard(fileId);
            setTimeout(() => setHighlightedCard(null), 2500);
        }
    };

    // Flatten BOM tree to get all nodes with hierarchy info
    // Uses qtyEdits to get user-modified quantities
    const flattenBOMTree = (node, level = 0, parentIndex = '') => {
        if (!node) return [];

        const currentIndex = parentIndex ? `${parentIndex}.${level + 1}` : `${level + 1}`;
        const nodeKey = node.part_number || node.file_id || `node-${level}`;
        // Use edited qty if available, otherwise fall back to node qty or default to 1
        const effectiveQty = qtyEdits[nodeKey] ?? node.qty ?? 1;

        const result = [{
            item: currentIndex,
            part_number: node.part_number || 'Unknown',
            description: node.description || '',
            qty: effectiveQty,
            status: node.status || 'missing',
            matched_file: node.matched_doc?.filename || '-',
            matched_doc: node.matched_doc
        }];

        if (node.children && node.children.length > 0) {
            node.children.forEach((child, idx) => {
                result.push(...flattenBOMTree(child, idx, currentIndex));
            });
        }

        return result;
    };

    // Export BOM to Excel
    const handleExportBOM = async () => {
        if (!analysis) return;

        setIsExporting(true);
        try {
            const assemblyTree = analysis.result?.assembly_tree || {};
            const rootNode = assemblyTree.root;

            // Flatten the tree
            let bomItems = [];
            if (rootNode) {
                bomItems = flattenBOMTree(rootNode);
            } else if (assemblyTree.all_nodes) {
                assemblyTree.all_nodes.forEach((node, idx) => {
                    bomItems.push(...flattenBOMTree(node, idx));
                });
            }

            // Include orphan components in export
            const exportOrphans = assemblyTree.orphan_components || [];
            if (exportOrphans.length > 0) {
                const startIdx = bomItems.length;
                exportOrphans.forEach((node, idx) => {
                    bomItems.push(...flattenBOMTree(node, startIdx + idx, 'S')); // 'S' prefix for Standalone
                });
            }

            // Filter out missing if checkbox is checked
            if (excludeMissing) {
                bomItems = bomItems.filter(item => item.status === 'matched');
            }

            // Create workbook
            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'SourceOptima';
            workbook.created = new Date();

            const worksheet = workbook.addWorksheet('BOM Structure');

            // Define columns
            worksheet.columns = [
                { header: 'Item', key: 'item', width: 12 },
                { header: 'Part Number', key: 'part_number', width: 25 },
                { header: 'Description', key: 'description', width: 40 },
                { header: 'Qty', key: 'qty', width: 8 },
                { header: 'Status', key: 'status', width: 12 },
                { header: 'Matched File', key: 'matched_file', width: 30 },
                { header: 'Notes', key: 'notes', width: 30 }
            ];

            // Style header row
            const headerRow = worksheet.getRow(1);
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF1E5045' }
            };
            headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
            headerRow.height = 24;

            // Add data rows
            bomItems.forEach((item) => {
                const row = worksheet.addRow({
                    item: item.item,
                    part_number: item.part_number,
                    description: item.description,
                    qty: item.qty,
                    status: item.status === 'matched' ? 'Matched' : 'Missing',
                    matched_file: item.matched_file,
                    notes: item.status === 'missing' ? 'No drawing uploaded' : ''
                });

                // Color code rows
                if (item.status === 'matched') {
                    row.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFDCFCE7' } // Light green
                    };
                } else {
                    row.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFEF3C7' } // Light amber
                    };
                }
            });

            // Add summary footer
            const matchedCount = assemblyTree.matched_count || 0;
            const missingCount = assemblyTree.missing_count || 0;
            const totalCount = matchedCount + missingCount;

            worksheet.addRow([]); // Empty row
            const summaryRow = worksheet.addRow({
                item: 'Summary:',
                part_number: `Total: ${excludeMissing ? matchedCount : totalCount}`,
                description: `Matched: ${matchedCount}`,
                qty: '',
                status: excludeMissing ? '' : `Missing: ${missingCount}`,
                matched_file: '',
                notes: ''
            });
            summaryRow.font = { bold: true };
            summaryRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE5E7EB' } // Light gray
            };

            // Add SourceOptima footer
            worksheet.addRow([]);
            const footerRow = worksheet.addRow(['Excel generated by SourceOptima']);
            footerRow.getCell(1).font = { size: 8, italic: true, color: { argb: 'FF808080' } };
            worksheet.mergeCells(`A${footerRow.number}:G${footerRow.number}`);

            // Auto-fit columns
            worksheet.columns.forEach((column) => {
                column.alignment = { vertical: 'middle', wrapText: true };
            });

            // Generate file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const fileName = `BOM_${folderName || analysis.metadata?.folder_name || 'Assembly'}_${new Date().toISOString().split('T')[0]}.xlsx`;
            saveAs(blob, fileName);

            toast.success('BOM exported successfully');
        } catch (err) {
            console.error('Failed to export BOM:', err);
            toast.error('Failed to export BOM to Excel');
        } finally {
            setIsExporting(false);
        }
    };

    const handleApprove = async () => {
        if (!analysis) return;

        setApproving(true);
        try {
            // Get file_ids from the analysis metadata
            const analysisFileIds = analysis.metadata?.file_ids || fileIds;

            const res = await axios.post(
                `${BASE_URL}/ocr/approve-assembly/`,
                {
                    file_ids: analysisFileIds,
                    assembly_tree: analysis.result?.assembly_tree,
                    corrections: [], // No corrections for now
                    qty_edits: qtyEdits // Include qty edits to persist
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('BOM approved! Running final classification...');

            // Poll for classification completion
            const classificationTaskId = res.data.task_id;
            await pollForClassification(classificationTaskId);

        } catch (err) {
            console.error('Failed to approve BOM:', err);
            toast.error(err.response?.data?.error || 'Failed to approve BOM');
            setApproving(false);
        }
    };

    const pollForClassification = async (taskId) => {
        let attempts = 0;
        const maxAttempts = 120; // 2 minutes at 1s intervals

        const poll = async () => {
            try {
                const res = await axios.get(
                    `${BASE_URL}/ocr/status/${taskId}/`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (res.data.status === 'SUCCESS') {
                    toast.success('Classification complete!');
                    // Navigate to results page
                    history.push({
                        pathname: `/assembly-results/${assemblyId}`,
                        state: { folderName, spacePath }
                    });
                    return;
                }

                if (res.data.status === 'FAILURE') {
                    toast.error('Classification failed');
                    setApproving(false);
                    return;
                }

                // Continue polling
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(poll, 1000);
                } else {
                    toast.error('Classification timed out');
                    setApproving(false);
                }
            } catch (err) {
                console.error('Poll error:', err);
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(poll, 2000);
                } else {
                    setApproving(false);
                }
            }
        };

        poll();
    };

    const handleSkipApproval = () => {
        // Skip BOM approval and go directly to results
        history.push({
            pathname: `/assembly-results/${assemblyId}`,
            state: { folderName, spacePath }
        });
    };

    const handleBack = () => {
        history.goBack();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#f6f4ed] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e5045] mx-auto" />
                    <p className="mt-4 text-gray-600">Loading BOM structure...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#f6f4ed] flex items-center justify-center">
                <div className="text-center">
                    <FiAlertCircle className="w-12 h-12 text-[#f26522] mx-auto" />
                    <p className="mt-4 text-[#f26522]">{error}</p>
                    <button
                        onClick={handleBack}
                        className="mt-4 px-4 py-2 bg-white hover:bg-gray-100 rounded-lg text-gray-700 border border-gray-200"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const result = analysis?.result || {};
    const metadata = analysis?.metadata || {};
    const assemblyTree = result.assembly_tree || {};
    const rootNode = assemblyTree.root;
    const matchedCount = assemblyTree.matched_count || 0;
    const missingCount = assemblyTree.missing_count || 0;
    const totalItems = matchedCount + missingCount;
    const matchRate = totalItems > 0 ? Math.round((matchedCount / totalItems) * 100) : 0;

    // Collect all matched documents for file cards section
    // Also tracks whether each doc is a "leaf" (no matched children) for weight calculations
    const collectMatchedDocs = (node) => {
        if (!node) return [];
        const docs = [];

        // Count matched children (not missing)
        const matchedChildren = (node.children || []).filter(c => c.status === 'matched');
        const hasMatchedChildren = matchedChildren.length > 0;

        if (node.status === 'matched' && node.matched_doc) {
            // Add flag to indicate if this is a leaf node (no matched children)
            // Only leaf nodes should have their weight counted to avoid double-counting
            // Include qty from node for weight calculations (extended weight = unit weight × qty)
            const qty = node.qty || 1;
            docs.push({
                ...node.matched_doc,
                is_leaf: !hasMatchedChildren,
                has_matched_children: hasMatchedChildren,
                matched_children_count: matchedChildren.length,
                qty: qty  // Quantity from BOM for extended weight calculation
            });
        }
        if (node.children) {
            node.children.forEach(child => {
                docs.push(...collectMatchedDocs(child));
            });
        }
        return docs;
    };

    let matchedDocs = [];
    if (rootNode) {
        matchedDocs = collectMatchedDocs(rootNode);
    } else if (assemblyTree.all_nodes) {
        assemblyTree.all_nodes.forEach(node => {
            matchedDocs.push(...collectMatchedDocs(node));
        });
    }

    // Also collect from orphan_components (standalone docs not in any BOM)
    const orphanComponents = assemblyTree.orphan_components || [];
    console.log('[BOM DEBUG] assemblyTree keys:', Object.keys(assemblyTree));
    console.log('[BOM DEBUG] orphan_components count:', orphanComponents.length);
    console.log('[BOM DEBUG] orphan_components:', orphanComponents.map(n => n.part_number));
    orphanComponents.forEach(node => {
        matchedDocs.push(...collectMatchedDocs(node));
    });

    // Deduplicate by file_id
    console.log('[BOM DEBUG] matchedDocs before dedup:', matchedDocs.length);
    const seenIds = new Set();
    matchedDocs = matchedDocs.filter(doc => {
        if (!doc.file_id || seenIds.has(doc.file_id)) return false;
        seenIds.add(doc.file_id);
        return true;
    });
    console.log('[BOM DEBUG] matchedDocs after dedup:', matchedDocs.length);

    // Compute material summary from matchedDocs for Material Weight Summary view
    const computeMaterialSummary = () => {
        const materialMap = {};
        let totalWeight = 0;
        let pdfsWithWeight = 0;

        // Debug: Log what we're processing
        console.log('[WEIGHT DEBUG] Processing', matchedDocs.length, 'matched docs');
        const weightContributions = [];

        matchedDocs.forEach(doc => {
            const unitWeight = parseFloat(doc.weight_kg) || 0;
            const qty = parseInt(doc.qty, 10) || 1;  // Quantity from BOM
            const extendedWeight = unitWeight * qty;  // Total weight = unit weight × qty

            // Only count weight from LEAF nodes (no matched children)
            // Parent assemblies with stated weights already include children's weights
            // This prevents double-counting when both parent and children have weights
            const isLeaf = doc.is_leaf !== false; // Default to true for backwards compatibility
            const shouldCountWeight = isLeaf && unitWeight > 0;

            // Debug: Track all weight contributions
            if (unitWeight > 0) {
                weightContributions.push({
                    filename: doc.filename,
                    part_number: doc.part_number,
                    unitWeight,
                    qty,
                    extendedWeight,
                    isLeaf,
                    shouldCountWeight,
                    has_matched_children: doc.has_matched_children
                });
            }

            if (shouldCountWeight) {
                pdfsWithWeight++;
                totalWeight += extendedWeight;  // Use extended weight (qty × unit)
            }

            // Determine material category
            const material = doc.material || '';
            const sec232Material = (doc.sec232_material || '').toLowerCase();
            const llmMaterialParent = (doc.material_parent || '').trim();

            // Valid LLM parent categories (must match exactly)
            const validParentCategories = [
                'Steel', 'Aluminum', 'Plastics/Polymers', 'Copper/Brass',
                'Rubber/Elastomers', 'Paper/Fiber', 'Glass/Ceramics',
                'Other Metals', 'Other/Unknown'
            ];

            // Priority: sec232_material > LLM material_parent > frontend normalization
            let materialName;
            if (sec232Material === 'steel') {
                materialName = 'Steel';
            } else if (sec232Material === 'aluminum' || sec232Material === 'aluminium') {
                materialName = 'Aluminum';
            } else if (llmMaterialParent && validParentCategories.includes(llmMaterialParent)) {
                // Use LLM-assigned parent category if valid
                materialName = llmMaterialParent;
            } else {
                // Fallback to frontend normalization for unrecognized/missing LLM values
                materialName = normalizeMaterialToParent(material);
            }

            if (!materialMap[materialName]) {
                materialMap[materialName] = { weight: 0, count: 0, components: [] };
            }
            // Only add weight for leaf nodes to avoid double-counting
            if (shouldCountWeight) {
                materialMap[materialName].weight += extendedWeight;  // Use extended weight
            }
            materialMap[materialName].count++;
            materialMap[materialName].components.push({
                file_id: doc.file_id,
                part_number: doc.part_number || doc.filename,
                filename: doc.filename,
                material: doc.material,
                unit_weight_kg: unitWeight,  // Unit weight from drawing
                qty: qty,  // Quantity from BOM
                weight_kg: shouldCountWeight ? extendedWeight : 0,  // Extended weight (qty × unit)
                hts_code: doc.hts_code,
                is_leaf: isLeaf,
                has_children: doc.has_matched_children || false
            });
        });

        // Convert to ordered array with percentages
        const orderedMaterials = Object.entries(materialMap)
            .map(([name, data]) => ({
                name,
                weight: data.weight,
                count: data.count,
                components: data.components.sort((a, b) => (b.weight_kg || 0) - (a.weight_kg || 0)),
                percentage: totalWeight > 0 ? (data.weight / totalWeight) * 100 : 0
            }))
            .sort((a, b) => {
                // Priority: Steel first, Aluminum second, then by weight
                if (a.name === 'Steel') return -1;
                if (b.name === 'Steel') return 1;
                if (a.name === 'Aluminum') return -1;
                if (b.name === 'Aluminum') return 1;
                return b.weight - a.weight;
            });

        // Debug: Output weight breakdown
        console.log('[WEIGHT DEBUG] === WEIGHT BREAKDOWN ===');
        console.log('[WEIGHT DEBUG] Total Weight:', totalWeight.toFixed(2), 'kg');
        console.log('[WEIGHT DEBUG] PDFs contributing weight:', pdfsWithWeight);
        console.log('[WEIGHT DEBUG] All docs with weight:', weightContributions.length);

        // Calculate sum WITHOUT qty multiplication for comparison
        const unitWeightSum = weightContributions.filter(c => c.shouldCountWeight).reduce((sum, c) => sum + c.unitWeight, 0);
        console.log('[WEIGHT DEBUG] Sum of UNIT weights (no qty multiply):', unitWeightSum.toFixed(2), 'kg');
        console.log('[WEIGHT DEBUG] Difference factor:', (totalWeight / unitWeightSum).toFixed(2), 'x');

        // Sort by extended weight and show top contributors
        const sorted = [...weightContributions].sort((a, b) => b.extendedWeight - a.extendedWeight);
        console.log('[WEIGHT DEBUG] Top 20 weight contributors:');
        sorted.slice(0, 20).forEach((c, i) => {
            console.log(`  ${i+1}. ${c.filename || c.part_number}: ${c.unitWeight}kg × qty=${c.qty} = ${c.extendedWeight.toFixed(2)}kg (isLeaf=${c.isLeaf}, counted=${c.shouldCountWeight})`);
        });

        // Show parts with qty > 1 to check for inflated quantities
        const highQtyParts = weightContributions.filter(c => c.qty > 1 && c.shouldCountWeight).sort((a, b) => b.qty - a.qty);
        if (highQtyParts.length > 0) {
            console.log('[WEIGHT DEBUG] Parts with qty > 1 (check for inflated values):');
            highQtyParts.slice(0, 15).forEach(c => {
                console.log(`  - ${c.filename || c.part_number}: qty=${c.qty}, unitWeight=${c.unitWeight}kg, extended=${c.extendedWeight.toFixed(2)}kg`);
            });
        }

        // Show items that were EXCLUDED (non-leaf assemblies)
        const excluded = weightContributions.filter(c => !c.shouldCountWeight);
        if (excluded.length > 0) {
            console.log('[WEIGHT DEBUG] EXCLUDED (non-leaf assemblies):');
            excluded.forEach(c => {
                console.log(`  - ${c.filename || c.part_number}: ${c.extendedWeight.toFixed(2)}kg (has_matched_children=${c.has_matched_children})`);
            });
        }
        console.log('[WEIGHT DEBUG] === END BREAKDOWN ===');

        return {
            summary: {
                pdfs_with_weight: pdfsWithWeight,
                total_analyzed: matchedDocs.length,
                total_weight: totalWeight
            },
            totalWeight,
            orderedMaterials
        };
    };

    const { summary: materialSummary, totalWeight, orderedMaterials } = computeMaterialSummary();

    return (
        <div className="min-h-screen bg-[#f6f4ed]">
            {/* Header */}
            <div className="bg-white border-b border-[#1e5045]/10 sticky top-0 z-10">
                <div className="px-6 py-4 flex items-center justify-between max-w-5xl mx-auto">
                    <button
                        onClick={handleBack}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        <FaArrowLeft className="w-4 h-4" />
                        <span>Back</span>
                    </button>
                    <div className="text-center">
                        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2 justify-center">
                            <IoCubeOutline className="w-6 h-6 text-[#1e5045]" />
                            BOM Review & Approval
                        </h1>
                        <p className="text-sm text-gray-500">
                            {folderName || metadata.folder_name}
                        </p>
                    </div>
                    <div className="w-20" /> {/* Spacer for centering */}
                </div>
            </div>

            {/* Main Content */}
            <div className="p-6 max-w-5xl mx-auto space-y-6">
                {/* Stats Banner */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1e5045] to-[#225953] flex items-center justify-center">
                                <FiPackage className="w-7 h-7 text-white" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">
                                    Assembly Bill of Materials
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Review the reconstructed BOM structure before final classification
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-[#94c43d]">{matchedCount}</div>
                                <div className="text-xs text-gray-500">Matched</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-bold text-[#f26522]">{missingCount}</div>
                                <div className="text-xs text-gray-500">Missing</div>
                            </div>
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${matchRate >= 80 ? 'text-[#94c43d]' : matchRate >= 50 ? 'text-[#f26522]' : 'text-red-500'}`}>
                                    {matchRate}%
                                </div>
                                <div className="text-xs text-gray-500">Match Rate</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Info Banner */}
                {missingCount > 0 && (
                    <div className="bg-[#f26522]/10 border border-[#f26522]/30 rounded-lg p-4 flex items-start gap-3">
                        <FaExclamationTriangle className="w-5 h-5 text-[#f26522] flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-[#f26522]">
                                {missingCount} BOM item{missingCount > 1 ? 's' : ''} could not be matched to uploaded documents
                            </p>
                            <p className="text-sm text-gray-700 mt-1">
                                Missing items may affect classification accuracy. You can still proceed with approval,
                                or go back to upload the missing files.
                            </p>
                        </div>
                    </div>
                )}

                {/* Construction Report Banner - Shows how BOM was built */}
                {result?.construction_report && (
                    <ConstructionReportBanner
                        report={result.construction_report}
                        isExpanded={reportExpanded}
                        onToggle={() => setReportExpanded(!reportExpanded)}
                    />
                )}

                {/* BOM Tree / Material View with Toggle */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {/* View Toggle Buttons */}
                            <div className="flex items-center bg-gray-100 rounded-lg p-1">
                                <button
                                    onClick={() => setViewMode('bom')}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                        viewMode === 'bom'
                                            ? 'bg-white text-[#1e5045] shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    <FiList className="w-4 h-4" />
                                    BOM Tree
                                </button>
                                <button
                                    onClick={() => setViewMode('material')}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                        viewMode === 'material'
                                            ? 'bg-white text-[#1e5045] shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    <FiLayers className="w-4 h-4" />
                                    Materials
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {viewMode === 'bom' && (
                                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={excludeMissing}
                                        onChange={(e) => setExcludeMissing(e.target.checked)}
                                        className="rounded border-gray-300 text-[#1e5045] focus:ring-[#1e5045]"
                                    />
                                    Do not include missing items
                                </label>
                            )}
                            <button
                                onClick={handleExportBOM}
                                disabled={isExporting}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    isExporting
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-[#1e5045] text-white hover:bg-[#163d35]'
                                }`}
                            >
                                {isExporting ? (
                                    <FiLoader className="w-4 h-4 animate-spin" />
                                ) : (
                                    <HiArrowDownTray className="w-4 h-4" />
                                )}
                                Export to Excel
                            </button>
                        </div>
                    </div>

                    {/* Material Weight Summary View */}
                    {viewMode === 'material' && orderedMaterials.length > 0 && (
                        <div className="p-4">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-[#1e5045]">
                                        Material Weight Summary
                                    </h3>
                                    <p className="text-sm text-[#225953]">
                                        Section 232 Focus: Steel & Aluminum Analysis
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Based on {materialSummary.pdfs_with_weight || 0} of {materialSummary.total_analyzed || 0} analyzed PDFs
                                    </p>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs text-gray-500 uppercase tracking-wide">Total Weight</div>
                                    <div className="text-2xl font-bold text-[#f26522]">
                                        {totalWeight.toFixed(2)} kg
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-3">
                                {orderedMaterials.map((mat) => {
                                    const matNameLower = mat.name.toLowerCase();
                                    const isSec232 = matNameLower === 'steel' || matNameLower === 'aluminum' || matNameLower === 'aluminium';
                                    const isExpanded = expandedMaterials[mat.name] || false;
                                    const toggleExpand = () => {
                                        setExpandedMaterials(prev => ({
                                            ...prev,
                                            [mat.name]: !prev[mat.name]
                                        }));
                                    };
                                    return (
                                        <div key={mat.name} className="rounded-lg overflow-hidden border border-gray-200">
                                            {/* Material Header Row - Clickable */}
                                            <div
                                                onClick={toggleExpand}
                                                className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                                                    isSec232
                                                        ? 'bg-[#f26522]/10 hover:bg-[#f26522]/15'
                                                        : 'bg-[#f6f4ed] hover:bg-gray-100'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    {/* Expand/Collapse Chevron */}
                                                    <span className="text-gray-500">
                                                        {isExpanded ? (
                                                            <FaChevronDown className="w-3 h-3" />
                                                        ) : (
                                                            <FaChevronRight className="w-3 h-3" />
                                                        )}
                                                    </span>
                                                    {isSec232 && (
                                                        <span className="text-xs font-medium text-[#f26522] bg-[#f26522]/20 px-2 py-0.5 rounded">
                                                            Sec 232
                                                        </span>
                                                    )}
                                                    <span className="font-semibold text-gray-900">{mat.name}</span>
                                                    <span className="text-xs text-gray-500 ml-2">
                                                        ({mat.count} parts)
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="text-gray-700 font-medium">
                                                        {mat.weight.toFixed(2)} kg
                                                    </span>
                                                    <span className={`text-sm px-2 py-0.5 rounded ${
                                                        isSec232 ? 'bg-[#f26522]/20 text-[#f26522]' : 'bg-gray-200 text-gray-600'
                                                    }`}>
                                                        {mat.percentage.toFixed(1)}%
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Expanded Component List */}
                                            {isExpanded && mat.components && mat.components.length > 0 && (
                                                <div className="bg-white border-t border-gray-200">
                                                    <table className="w-full text-sm">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="text-left px-3 py-2 text-gray-600 font-medium">Part Number</th>
                                                                <th className="text-left px-3 py-2 text-gray-600 font-medium">Material</th>
                                                                <th className="text-center px-3 py-2 text-gray-600 font-medium">Qty</th>
                                                                <th className="text-right px-3 py-2 text-gray-600 font-medium" title="Unit weight × Qty">Ext. Weight</th>
                                                                <th className="text-left px-3 py-2 text-gray-600 font-medium">HTS Code</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {mat.components.map((comp, idx) => (
                                                                <tr
                                                                    key={comp.file_id || idx}
                                                                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (comp.file_id) {
                                                                            setHighlightedCard(comp.file_id);
                                                                            setViewMode('bom');
                                                                            // Scroll to file cards section
                                                                            setTimeout(() => {
                                                                                const el = document.getElementById(`file-card-${comp.file_id}`);
                                                                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                                            }, 100);
                                                                        }
                                                                    }}
                                                                >
                                                                    <td className="px-3 py-2 text-gray-900">
                                                                        {comp.part_number || '-'}
                                                                        {comp.has_children && (
                                                                            <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded" title="Assembly - weight included in children">
                                                                                ASSY
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-gray-600 truncate max-w-[200px]" title={comp.material}>
                                                                        {comp.material || '-'}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-center text-gray-700">
                                                                        {comp.qty || 1}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-right text-gray-700">
                                                                        {comp.has_children ? (
                                                                            <span className="text-gray-400 italic" title="Weight not counted - included in child components">
                                                                                (in children)
                                                                            </span>
                                                                        ) : comp.weight_kg ? (
                                                                            <span title={comp.unit_weight_kg ? `${comp.unit_weight_kg.toFixed(2)} kg × ${comp.qty || 1}` : ''}>
                                                                                {comp.weight_kg.toFixed(2)} kg
                                                                            </span>
                                                                        ) : '-'}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-gray-600">{comp.hts_code || '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Empty Material View */}
                    {viewMode === 'material' && orderedMaterials.length === 0 && (
                        <div className="p-8 text-center text-gray-500">
                            <FiLayers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No material data available</p>
                            <p className="text-sm mt-1">
                                Material information will appear here once documents are analyzed.
                            </p>
                        </div>
                    )}

                    {/* BOM Tree View */}
                    {viewMode === 'bom' && (
                    <div className="p-4 max-h-[500px] overflow-y-auto">
                        {rootNode ? (
                            <BOMTreeNode
                                node={rootNode}
                                onNodeClick={handleNodeClick}
                                qtyEdits={qtyEdits}
                                onQtyChange={handleQtyChange}
                                isRoot={true}
                                userSpecifiedRoot={assemblyTree.user_specified_root || false}
                            />
                        ) : assemblyTree.all_nodes && assemblyTree.all_nodes.length > 0 ? (
                            <div className="space-y-2">
                                {assemblyTree.all_nodes.map((node, idx) => (
                                    <BOMTreeNode
                                        key={`${node.part_number}-${idx}`}
                                        node={node}
                                        onNodeClick={handleNodeClick}
                                        qtyEdits={qtyEdits}
                                        onQtyChange={handleQtyChange}
                                    />
                                ))}
                            </div>
                        ) : orphanComponents.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <FiPackage className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No BOM structure detected</p>
                                <p className="text-sm mt-1">
                                    The uploaded documents did not contain BOM tables.
                                </p>
                            </div>
                        ) : null}

                        {/* Orphan Components - Standalone docs not in any BOM */}
                        {orphanComponents.length > 0 && (
                            <div className={rootNode || (assemblyTree.all_nodes && assemblyTree.all_nodes.length > 0) ? "mt-6 pt-4 border-t border-gray-200" : ""}>
                                <div className="flex items-center gap-2 mb-3">
                                    <FiFile className="w-4 h-4 text-[#1e5045]" />
                                    <span className="text-sm font-medium text-gray-700">
                                        Standalone Components ({orphanComponents.length})
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        - Not referenced in any BOM
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {orphanComponents.map((node, idx) => (
                                        <BOMTreeNode
                                            key={`orphan-${node.part_number}-${idx}`}
                                            node={node}
                                            onNodeClick={handleNodeClick}
                                            qtyEdits={qtyEdits}
                                            onQtyChange={handleQtyChange}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                </div>

                {/* File Cards Section */}
                {matchedDocs.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-4 border-b border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <FiFile className="w-5 h-5 text-[#1e5045]" />
                                Matched File Cards
                                <span className="text-sm font-normal text-gray-500">
                                    ({matchedDocs.length} files)
                                </span>
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Click on a matched BOM item above to jump to its file card
                            </p>
                        </div>
                        <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
                            {matchedDocs.map((doc) => (
                                <FileCard
                                    key={doc.file_id}
                                    doc={doc}
                                    highlighted={highlightedCard === doc.file_id}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-[#1e5045]/10 p-4">
                    <button
                        onClick={handleSkipApproval}
                        className="px-4 py-2 text-gray-600 hover:text-[#1e5045] hover:bg-[#f6f4ed] rounded-lg transition-colors"
                    >
                        Skip to Results
                    </button>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleBack}
                            className="px-4 py-2 bg-[#f6f4ed] text-gray-700 rounded-lg hover:bg-gray-200 transition-colors border border-[#1e5045]/10"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApprove}
                            disabled={approving}
                            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                                approving
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-[#1e5045] text-white hover:bg-[#163d35]'
                            }`}
                        >
                            {approving ? (
                                <>
                                    <FiLoader className="w-4 h-4 animate-spin" />
                                    <span>Approving...</span>
                                </>
                            ) : (
                                <>
                                    <FaCheck className="w-4 h-4" />
                                    <span>Approve BOM</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
